import { clsx } from "clsx";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { MeasurementDialog } from "@/features/measurements/components/measurement-dialog";
import { useLatestMeasurements } from "@/features/measurements/hooks/use-latest-measurements.ts";
import type { MeasurementTarget } from "@/features/measurements/types.ts";
import { GeneralNotesStrip } from "@/features/schedule-notes/components/general-notes-strip";
import { NotesButton } from "@/features/schedule-notes/components/notes-button";
import {
	NotesPanel,
	type NotesStageFilter,
} from "@/features/schedule-notes/components/notes-panel";
import { useScheduleNotes } from "@/features/schedule-notes/hooks/use-schedule-notes.ts";
import type { Note } from "@/features/schedule-notes/types.ts";
import {
	generalOpen,
	highestSeverityByStage,
	openBySeverity,
	openTotal,
	subtreeIds,
} from "@/features/schedule-notes/utils/summary.ts";
import { usePrint } from "@/hooks/use-print.ts";
import { currentWorkDate, currentWorkDay } from "@/lib/format.ts";
import { useCronogramaView } from "@/store/use-cronograma-view.ts";
import { useFullscreen } from "../../hooks/use-fullscreen.ts";
import type {
	ConstructionSummary,
	ScheduleNode,
	ScheduleSpan,
} from "../../types.ts";
import { tone } from "../../utils/colors.ts";
import { delayByStage } from "../../utils/delay.ts";
import { progressByStage } from "../../utils/progress.ts";
import {
	buildScaleDomain,
	dayCells,
	leftEdgeDate,
	monthCells,
	PX_PER_DAY,
	scrollLeftFor,
	type TimeScale,
	weekCells,
} from "../../utils/scale.ts";
import { todayIndex } from "../../utils/timeline.ts";
import {
	childrenCount,
	topLevelIndex,
	visibleNodes,
} from "../../utils/tree.ts";
import { ActualDatesDialog } from "../actual-dates-dialog";
import { type ScheduleDraft, ScheduleEditor } from "../schedule-editor";
import { SchedulePrint } from "../schedule-print";
import { GanttAxis } from "./axis.tsx";
import { GanttLegend } from "./legend.tsx";
import {
	type DragState,
	GanttRow,
	type RowModel,
	SCROLL_EDGE,
} from "./row.tsx";
import { GanttToolbar } from "./toolbar.tsx";

const EMPTY_NOTES: Note[] = [];

export function ScheduleGanttSkeleton() {
	return (
		<Card className="schedule-gantt__card">
			<div className="schedule-gantt__skeleton">
				{Array.from({ length: 5 }, (_, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: static placeholder list
					<Skeleton key={index} className="schedule-gantt__skeleton-row" />
				))}
			</div>
		</Card>
	);
}

export function ScheduleGantt({
	constructionId,
	construction,
	nodes,
	span,
	canManage,
	canMeasure,
	canViewNotes,
	canManageNotes,
}: {
	constructionId: string;
	construction: ConstructionSummary;
	nodes: ScheduleNode[];
	span: ScheduleSpan;
	canManage: boolean;
	canMeasure: boolean;
	canViewNotes: boolean;
	canManageNotes: boolean;
}) {
	const scale = useCronogramaView((state) => state.scale);
	const scheme = useCronogramaView((state) => state.scheme);
	const legend = useCronogramaView((state) => state.legend);
	const setScale = useCronogramaView((state) => state.setScale);
	const setScheme = useCronogramaView((state) => state.setScheme);
	const setLegend = useCronogramaView((state) => state.setLegend);

	const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
	const [stageToMeasure, setStageToMeasure] =
		useState<MeasurementTarget | null>(null);
	const [actualTarget, setActualTarget] = useState<ScheduleNode | null>(null);
	const [draft, setDraft] = useState<ScheduleDraft | null>(null);
	const [editorOpen, setEditorOpen] = useState(false);
	const [preview, setPreview] = useState<ScheduleDraft | null>(null);
	const [drag, setDrag] = useState<DragState | null>(null);
	const [extraBefore, setExtraBefore] = useState(0);
	const [extraAfter, setExtraAfter] = useState(0);
	const [notesOpen, setNotesOpen] = useState(false);
	const [stageFilter, setStageFilter] = useState<NotesStageFilter | null>(null);

	const scrollRef = useRef<HTMLDivElement>(null);
	const previousStartRef = useRef<number | null>(null);
	const extendLockRef = useRef(false);
	const pendingScrollRef = useRef<number | null>(null);
	const anchorRef = useRef<string | null>(null);
	const savedScrollRef = useRef<number | null>(null);

	const fullscreen = useFullscreen();
	const printer = usePrint();
	const measurements = useLatestMeasurements(constructionId);
	const notesQuery = useScheduleNotes(
		canViewNotes ? constructionId : undefined,
	);
	const notes = notesQuery.data ?? EMPTY_NOTES;

	const today = useMemo(() => currentWorkDay(), []);
	const todayKey = useMemo(() => currentWorkDate(), []);
	const px = PX_PER_DAY[scale];
	const domain = useMemo(
		() =>
			buildScaleDomain(
				[
					span.start,
					span.end,
					construction.startDate,
					construction.expectedEndDate,
				],
				today,
				scale,
				extraBefore,
				extraAfter,
			),
		[span, construction, today, scale, extraBefore, extraAfter],
	);

	const counts = useMemo(() => childrenCount(nodes), [nodes]);
	const delays = useMemo(
		() => delayByStage(nodes, todayKey, counts),
		[nodes, todayKey, counts],
	);
	const percents = useMemo(
		() => progressByStage(nodes, measurements.data, counts),
		[nodes, measurements.data, counts],
	);
	const topIndexes = useMemo(() => topLevelIndex(nodes), [nodes]);
	const openCount = useMemo(() => openTotal(openBySeverity(notes)), [notes]);
	const rowSeverity = useMemo(
		() => highestSeverityByStage(notes, nodes),
		[notes, nodes],
	);
	const generalNotes = useMemo(() => generalOpen(notes), [notes]);
	const topLevelNames = useMemo(
		() => nodes.filter((node) => node.depth === 0).map((node) => node.name),
		[nodes],
	);

	const toRow = useCallback(
		(node: ScheduleNode): RowModel => {
			const childCount = counts.get(node.id) ?? 0;
			const isLeaf = childCount === 0;
			const delay = delays.get(node.id) ?? { kind: "none", days: 0 };
			const percent = percents.get(node.id) ?? 0;
			return {
				node,
				childCount,
				isLeaf,
				collapsed: collapsed.has(node.id),
				delay,
				percent,
				tone: tone({
					node,
					isLeaf,
					delay,
					scheme,
					topIndex: topIndexes.get(node.id) ?? 0,
					percent,
				}),
				severity: rowSeverity.get(node.id),
			};
		},
		[counts, delays, percents, collapsed, scheme, topIndexes, rowSeverity],
	);

	const rows = useMemo(
		() => visibleNodes(nodes, collapsed).map(toRow),
		[nodes, collapsed, toRow],
	);
	const allRows = useMemo(() => nodes.map(toRow), [nodes, toRow]);
	const delayedCount = useMemo(
		() =>
			nodes.filter(
				(node) =>
					(counts.get(node.id) ?? 0) === 0 &&
					delays.get(node.id)?.kind === "delayed",
			).length,
		[nodes, counts, delays],
	);

	const months = useMemo(() => monthCells(domain), [domain]);
	const cells = useMemo(
		() =>
			scale === "days" ? dayCells(domain, today) : weekCells(domain, today),
		[domain, scale, today],
	);
	const todayAt = todayIndex(domain, today);

	const handlePreview = useCallback(
		(next: ScheduleDraft | null) => setPreview(next),
		[],
	);

	const requestExtend = useCallback((side: "before" | "after") => {
		if (extendLockRef.current) return;
		extendLockRef.current = true;
		if (side === "before") {
			setExtraBefore((value) => value + 1);
		} else {
			setExtraAfter((value) => value + 1);
		}
	}, []);

	const fillScrollRoom = useCallback(() => {
		const scroll = scrollRef.current;
		if (!scroll || scroll.clientWidth === 0) return;
		if (scroll.scrollWidth - scroll.clientWidth < SCROLL_EDGE * 2) {
			requestExtend("after");
		}
	}, [requestExtend]);

	useLayoutEffect(() => {
		const scroll = scrollRef.current;
		if (!scroll) return;
		const previous = previousStartRef.current;
		previousStartRef.current = domain.start.getTime();
		const anchor = anchorRef.current;
		if (anchor !== null) {
			anchorRef.current = null;
			scroll.scrollLeft = scrollLeftFor(domain, anchor, scale);
		} else if (previous === null) {
			if (todayAt !== null) {
				scroll.scrollLeft = Math.max(0, todayAt * px - scroll.clientWidth / 3);
			}
		} else {
			const deltaDays = Math.round(
				(previous - domain.start.getTime()) / 86_400_000,
			);
			const shift = deltaDays > 0 ? deltaDays * px : 0;
			if (shift) scroll.scrollLeft += shift;
			if (pendingScrollRef.current !== null) {
				scroll.scrollTo({
					left: pendingScrollRef.current + shift,
					behavior: "smooth",
				});
				pendingScrollRef.current = null;
			}
		}
		extendLockRef.current = false;
		fillScrollRoom();
	}, [domain, todayAt, px, scale, fillScrollRoom]);

	useEffect(() => {
		window.addEventListener("resize", fillScrollRoom);
		return () => window.removeEventListener("resize", fillScrollRoom);
	}, [fillScrollRoom]);

	useLayoutEffect(() => {
		if (fullscreen.active) return;
		const saved = savedScrollRef.current;
		if (saved === null) return;
		savedScrollRef.current = null;
		if (scrollRef.current) scrollRef.current.scrollLeft = saved;
	}, [fullscreen.active]);

	function handleScroll() {
		const scroll = scrollRef.current;
		if (!scroll) return;
		if (scroll.scrollLeft < SCROLL_EDGE) {
			requestExtend("before");
		} else if (
			scroll.scrollLeft + scroll.clientWidth >
			scroll.scrollWidth - SCROLL_EDGE
		) {
			requestExtend("after");
		}
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies(nodes.length): re-attach once the empty state swaps to the scrollable grid
	useEffect(() => {
		const scroll = scrollRef.current;
		if (!scroll) return;
		function onWheel(event: WheelEvent) {
			if (!scroll) return;
			if (event.deltaX === 0 && event.deltaY !== 0 && !event.ctrlKey) {
				event.preventDefault();
				scroll.scrollLeft += event.deltaY;
				if (event.deltaY < 0 && scroll.scrollLeft <= 0) {
					requestExtend("before");
				}
				return;
			}
			if (event.deltaX < 0 && scroll.scrollLeft <= 0) {
				requestExtend("before");
			}
		}
		scroll.addEventListener("wheel", onWheel, { passive: false });
		return () => scroll.removeEventListener("wheel", onWheel);
	}, [requestExtend, nodes.length]);

	function smoothScrollTo(target: number) {
		const scroll = scrollRef.current;
		if (!scroll) return;
		const clamped = Math.max(0, target);
		if (clamped < SCROLL_EDGE) {
			pendingScrollRef.current = clamped;
			requestExtend("before");
			return;
		}
		if (clamped + scroll.clientWidth > scroll.scrollWidth - SCROLL_EDGE) {
			pendingScrollRef.current = clamped;
			requestExtend("after");
			return;
		}
		scroll.scrollTo({ left: clamped, behavior: "smooth" });
	}

	function scrollByMonth(direction: -1 | 1) {
		const scroll = scrollRef.current;
		if (!scroll) return;
		smoothScrollTo(scroll.scrollLeft + direction * 30 * px);
	}

	function scrollToToday() {
		const scroll = scrollRef.current;
		if (!scroll || todayAt === null) return;
		smoothScrollTo(todayAt * px - scroll.clientWidth / 3);
	}

	function changeScale(next: TimeScale) {
		if (next === scale) return;
		const scroll = scrollRef.current;
		if (scroll)
			anchorRef.current = leftEdgeDate(domain, scroll.scrollLeft, scale);
		setScale(next);
	}

	async function toggleFullscreen() {
		if (fullscreen.active) {
			await fullscreen.exit();
			return;
		}
		savedScrollRef.current = scrollRef.current?.scrollLeft ?? null;
		await fullscreen.enter();
	}

	useEffect(() => {
		if (!drag) return;
		function onKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") setDrag(null);
		}
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [drag]);

	function toggle(id: string) {
		setCollapsed((current) => {
			const next = new Set(current);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}

	function openDraft(next: ScheduleDraft) {
		setDraft(next);
		setEditorOpen(true);
	}

	function openNotes() {
		setStageFilter(null);
		setNotesOpen(true);
	}

	function openNotesFor(node: ScheduleNode) {
		setStageFilter({
			id: node.id,
			name: node.name,
			ids: subtreeIds(nodes, node.id),
		});
		setNotesOpen(true);
	}

	if (nodes.length === 0) {
		return (
			<Card className="schedule-gantt__card">
				<p className="schedule-gantt__empty">
					Nenhuma etapa cadastrada. Crie etapas para planejar o cronograma.
				</p>
			</Card>
		);
	}

	return (
		<div className="schedule-gantt" data-scale={scale}>
			<Card
				className={clsx(
					"schedule-gantt__card",
					fullscreen.active &&
						(fullscreen.fallback ? "is-fullscreen-fallback" : "is-fullscreen"),
				)}
				data-testid="schedule-gantt"
			>
				<GanttToolbar
					scale={scale}
					onScale={changeScale}
					scheme={scheme}
					onScheme={setScheme}
					legend={legend}
					onLegend={setLegend}
					delayedCount={delayedCount}
					notesButton={
						canViewNotes ? (
							<NotesButton openCount={openCount} onClick={openNotes} />
						) : undefined
					}
					fullscreen={fullscreen.active}
					onFullscreen={toggleFullscreen}
					onPrint={printer.print}
					onPrevious={() => scrollByMonth(-1)}
					onNext={() => scrollByMonth(1)}
					onToday={scrollToToday}
				/>
				<div
					className="schedule-gantt__scroll"
					ref={scrollRef}
					onScroll={handleScroll}
				>
					<div className="schedule-gantt__grid">
						<div className="schedule-gantt__head">
							<div className="schedule-gantt__corner">
								<span className="schedule-gantt__corner-name">Etapa</span>
								<span className="schedule-gantt__corner-total">
									Total de dias
								</span>
							</div>
							<GanttAxis
								domain={domain}
								scale={scale}
								months={months}
								cells={cells}
								todayAt={todayAt}
							/>
						</div>
						<div className="schedule-gantt__body">
							{rows.map((row) => (
								<GanttRow
									key={row.node.id}
									row={row}
									domain={domain}
									scale={scale}
									canManage={canManage}
									canMeasure={canMeasure}
									todayAt={todayAt}
									todayKey={todayKey}
									drag={drag}
									preview={preview}
									scrollRef={scrollRef}
									onDragChange={setDrag}
									onDraft={openDraft}
									onExtend={requestExtend}
									onActual={setActualTarget}
									onToggle={toggle}
									onNotes={openNotesFor}
									onMeasure={(node) =>
										setStageToMeasure({ id: node.id, name: node.name })
									}
								/>
							))}
						</div>
					</div>
				</div>
				{canViewNotes && (
					<GeneralNotesStrip notes={generalNotes} onViewAll={openNotes} />
				)}
				{legend && (
					<GanttLegend scheme={scheme} topLevelNames={topLevelNames} />
				)}
			</Card>

			{canViewNotes && (
				<NotesPanel
					open={notesOpen}
					onOpenChange={setNotesOpen}
					constructionId={constructionId}
					notes={notes}
					isPending={notesQuery.isPending}
					isError={notesQuery.isError}
					stages={nodes}
					stageFilter={stageFilter}
					onClearFilter={() => setStageFilter(null)}
					canManage={canManageNotes}
				/>
			)}

			{canManage && (
				<ScheduleEditor
					open={editorOpen}
					onOpenChange={setEditorOpen}
					constructionId={constructionId}
					nodes={nodes}
					draft={draft}
					onPreview={handlePreview}
				/>
			)}

			{canMeasure && (
				<>
					<MeasurementDialog
						open={!!stageToMeasure}
						onOpenChange={(open) => {
							if (!open) setStageToMeasure(null);
						}}
						constructionId={constructionId}
						stage={stageToMeasure}
					/>
					<ActualDatesDialog
						open={!!actualTarget}
						onOpenChange={(open) => {
							if (!open) setActualTarget(null);
						}}
						constructionId={constructionId}
						node={actualTarget}
					/>
				</>
			)}

			{printer.printing && (
				<SchedulePrint
					construction={construction}
					span={span}
					rows={allRows}
					scheme={scheme}
					topLevelNames={topLevelNames}
					today={today}
					todayKey={todayKey}
					notes={notes}
				/>
			)}
		</div>
	);
}
